import React from 'react'
import styled, { css } from 'styled-components'
import { accents, brand, gray } from 'sutra/tokens/colors'

export type PillFamily =
  | 'coral'
  | 'gray'
  | 'emerald'
  | 'violet'
  | 'crimson'
  | 'golden'
  | 'blue'

export interface PillProps {
  $family?: PillFamily
  dot?: boolean
  children?: React.ReactNode
  className?: string
  style?: React.CSSProperties
}

const familyStyles: Record<PillFamily, ReturnType<typeof css>> = {
  coral: css`
    background: ${brand.sunriseCoral[50]};
    color: ${brand.sunriseCoral[600]};
  `,
  gray: css`
    background: #f5f5f5;
    color: #414651;
    border: 1px solid #e9eaeb;
  `,
  emerald: css`
    background: ${accents.emeraldAura[50]};
    color: ${accents.emeraldAura[600]};
  `,
  violet: css`
    background: ${accents.violetHorizon[50]};
    color: ${accents.violetHorizon[600]};
  `,
  crimson: css`
    background: ${accents.crimsonBloom[50]};
    color: ${accents.crimsonBloom[600]};
  `,
  golden: css`
    background: ${accents.goldenLight[50]};
    color: ${accents.goldenLight[600]};
  `,
  blue: css`
    background: ${accents.reflectionBlue[50]};
    color: ${gray[900]};
  `,
}

const PillBase = styled.span<{ $family: PillFamily; $dot: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: ${({ $dot }) => ($dot ? '6px' : '0')};
  font-size: 12px;
  font-weight: 500;
  padding: 3px 10px;
  border-radius: 9999px;
  line-height: 20px;
  ${({ $family }) => familyStyles[$family]}

  ${({ $dot }) =>
    $dot &&
    css`
      &::before {
        content: '';
        width: 6px;
        height: 6px;
        border-radius: 50%;
        background: currentColor;
        opacity: 0.7;
        flex-shrink: 0;
      }
    `}
`

export const Pill = ({
  $family = 'coral',
  dot = false,
  children,
  className,
  style,
}: PillProps): React.ReactElement => (
  <PillBase $family={$family} $dot={dot} className={className} style={style}>
    {children}
  </PillBase>
)

Pill.displayName = 'Pill'
